import { newRequestId } from "./requestId";

export interface GiftCardPurchaseRequest {
  purchase_request_id: string;
  company_id: string;
  value: number;
  payment_method_id: string;
  recipient_email?: string;
  recipient_name?: string;
  message?: string;
}

export interface PendingGiftCardPurchase {
  userId: string;
  request: GiftCardPurchaseRequest;
  fees: number;
  cardLabel: string;
}

const storageKey = (userId: string, companyId: string) =>
  `pointment-gift-card-purchase:${userId}:${companyId}`;

const UUID =
  /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;

const STORAGE_REQUIRED =
  "Allow browser storage before buying a gift card, " +
  "so your purchase can be recovered if the connection is interrupted.";

export const hasPendingGiftCardPurchase = (
  userId?: string,
  companyId?: string | null
) => {
  if (!userId || !companyId) return false;
  try {
    return !!sessionStorage.getItem(storageKey(userId, companyId));
  } catch {
    return false;
  }
};

// Keep the entire submitted request, so a reload or a changed default payment
// method cannot accidentally change a purchase whose payment outcome is unknown.
export const getPendingGiftCardPurchase = (
  userId: string,
  companyId: string
): PendingGiftCardPurchase | null => {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(storageKey(userId, companyId));
  } catch {
    throw new Error(STORAGE_REQUIRED);
  }
  if (!stored) return null;
  try {
    const pending = JSON.parse(stored);
    const request = pending.request;
    if (
      pending.userId !== userId ||
      request?.company_id !== companyId ||
      !UUID.test(request?.purchase_request_id) ||
      typeof request.payment_method_id !== "string" ||
      !request.payment_method_id ||
      !Number.isFinite(request.value) ||
      request.value < 1 ||
      request.value > 1000 ||
      (request.recipient_email !== undefined &&
        typeof request.recipient_email !== "string") ||
      (request.recipient_name !== undefined &&
        typeof request.recipient_name !== "string") ||
      (request.message !== undefined && typeof request.message !== "string") ||
      !Number.isFinite(pending.fees) ||
      pending.fees < 0 ||
      typeof pending.cardLabel !== "string"
    ) {
      throw new Error("Invalid saved purchase");
    }
    return pending;
  } catch {
    throw new Error(
      "We could not read your previous purchase. Contact the salon before buying another gift card."
    );
  }
};

export const saveGiftCardPurchase = (
  userId: string,
  request: Omit<GiftCardPurchaseRequest, "purchase_request_id">,
  display: Pick<PendingGiftCardPurchase, "fees" | "cardLabel">
): PendingGiftCardPurchase => {
  const existing = getPendingGiftCardPurchase(userId, request.company_id);
  if (existing) return existing;
  const pending: PendingGiftCardPurchase = {
    userId,
    request: { ...request, purchase_request_id: newRequestId() },
    ...display
  };
  const key = storageKey(userId, request.company_id);
  const stored = JSON.stringify(pending);
  try {
    sessionStorage.setItem(key, stored);
    if (sessionStorage.getItem(key) !== stored) throw new Error("Not saved");
  } catch {
    // Do not send a charge if its recovery identity cannot survive a reload.
    throw new Error(STORAGE_REQUIRED);
  }
  return pending;
};

// A delayed response must never clear a different, newer purchase.
export const clearGiftCardPurchase = (
  userId: string,
  companyId: string,
  requestId: string
) => {
  try {
    if (
      getPendingGiftCardPurchase(userId, companyId)?.request
        .purchase_request_id === requestId
    ) {
      sessionStorage.removeItem(storageKey(userId, companyId));
    }
  } catch {
    // Retaining a completed request is safe: the server replays its result.
  }
};
